/* ============================================
   TYPES — Database Schema TypeScript Types
   ============================================ */

import type { MatchResult } from './matching/types'

/** Slider-Antworten: Frage-ID -> Wert (-5 bis +5). */
export type SliderAntworten = Record<string, number>

// ---- Pflegekraft ----
export interface Pflegekraft {
  id: string
  created_at: string
  updated_at: string

  // Grunddaten
  vorname: string
  nachname: string
  telefon: string | null
  telefon_vorwahl: string | null
  email: string
  plz: string | null
  wohnort: string | null
  bundesland: string | null
  fuehrerschein: boolean | null
  eigener_pkw: boolean | null

  // Berufliches Profil
  qualifikation: string | null
  qualifikation_sonstiges: string | null
  examen: boolean | null
  anerkennungsstatus: string | null
  berufserfahrung_jahre: number | null
  aktueller_bereich: string | null
  aktueller_bereich_sonstiges: string | null
  derzeit_beschaeftigt: boolean | null
  aktuelles_arbeitsmodell: string | null

  // Wechselwünsche / Rahmenbedingungen
  gewuenschter_bereich: string | null
  gewuenschte_bereiche: string[] | null
  gewuenschte_wochenstunden: number | null
  gewuenschtes_arbeitsmodell: string | null
  gewuenschte_arbeitsmodelle: string[] | null
  fruehester_wechselzeitpunkt: string | null
  max_arbeitsweg_minuten: number | null
  max_arbeitsweg_km: number | null
  wunschregionen: string[] | null
  umzug_moeglich: boolean | null
  umzug_wohin: string | null
  gehaltswunsch_brutto: number | null
  mindestgehalt_brutto: number | null

  // Schicht-Bereitschaft ('ja' | 'nein' | 'optional')
  fruehdienst_moeglich: string | null
  spaetdienst_moeglich: string | null
  nachtdienst_moeglich: string | null
  wochenenddienst_moeglich: string | null
  geteilte_dienste_ok: string | null
  rufbereitschaft_ok: string | null
  wochenendbereitschaft: boolean | null
  feste_dienstplaene_wichtig: boolean | null
  familienfreundlichkeit_wichtig: boolean | null

  // No-Gos & Slider-Profil
  no_gos: string[] | null
  slider_antworten: SliderAntworten | null

  // Wechselbegleitung
  trial_day_interesse: string | null
  bewerbungs_support: boolean | null
  kuendigungs_support: boolean | null
  umzugs_support: string | null
  anerkennungs_support: string | null

  // Freitexte
  freitext_ausschluss: string | null
  freitext_wichtig: string | null

  // Einwilligungen
  einwilligung_datenschutz: boolean
  einwilligung_kontakt: boolean
  einwilligung_anonymisiert: boolean

  // Prozessdaten
  status: ProfilStatus
  interner_ansprechpartner: string | null
  letzter_kontakt: string | null
  followup_datum: string | null
  interne_notizen: string | null
}

export type ProfilStatus =
  | 'neu'
  | 'geprueft'
  | 'matchbar'
  | 'in_vermittlung'
  | 'vorgestellt'
  | 'interview'
  | 'platziert'
  | 'abgesagt'

// ---- Sub-Tabellen Pflegekraft ----
export interface PflegekraftWechselgrund {
  id: string
  pflegekraft_id: string
  grund: string
  sonstiges_text: string | null
  intensitaet: number | null
  top_rang: number | null
  created_at: string
}

export interface PflegekraftSchichtwunsch {
  id: string
  pflegekraft_id: string
  schicht: string
}

export interface PflegekraftPrioritaet {
  id: string
  pflegekraft_id: string
  prioritaet: string
  rang: number
}

// ---- Arbeitgeber ----
export interface Arbeitgeber {
  id: string
  created_at: string
  updated_at: string

  name: string
  einrichtungstyp: string | null
  traeger: string | null
  traegerart: string | null
  fachbereich: string | null
  mitarbeiter_anzahl: number | null
  pflegekraefte_anzahl: number | null
  plz: string | null
  ort: string | null
  bundesland: string | null
  website: string | null
  karriereseite: string | null
  bemerkungen: string | null
  herkunft: string | null

  // Matching-relevante Attribute (Alt-Schema)
  arbeitsmodelle: string[] | null
  schichtmodell: string | null
  nachtdienst_erforderlich: string
  teilzeit_moeglich: string
  wunschqualifikationen: string[] | null
  gehaltsrahmen_min: number | null
  gehaltsrahmen_max: number | null
  kurzfristige_wechsel: boolean | null
  dienstplansicherheit: string
  familienfreundlich: string
  einarbeitung_gut: string
  fortbildungen: boolean | null
  benefits: string[] | null
  offen_fuer_direktansprache: boolean

  // Matching v2 — Einrichtungsprofil
  einsatzbereiche: string[] | null
  mindestberufserfahrung: number | null
  anerkennung_erforderlich: string | null
  sprachlevel: string | null
  wochenstunden_min: number | null
  wochenstunden_max: number | null
  wochenenddienst_erforderlich: string | null
  geteilte_dienste: string | null
  rufbereitschaft: string | null
  zuschlaege: string | null
  slider_antworten: SliderAntworten | null
  fakten: Record<string, unknown> | null
  besonderheiten: string | null
  onboarding_dauer_wochen: number | null
  feste_ansprechperson: boolean | null
  hospitation_moeglich: boolean | null
  umzugshilfe: string | null
  anerkennungshilfe: string | null

  // Interner Fit-Score
  reaktionsgeschwindigkeit: number | null
  transparenz: number | null
  gespraechsqualitaet: number | null
  zuverlaessigkeit: number | null
  langfristige_eignung: number | null
  fluktuationsrisiko: string | null

  status: string
  interne_notizen: string | null
}

export interface ArbeitgeberStandort {
  id: string
  arbeitgeber_id: string
  name: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  bundesland: string | null
  lat: number | null
  lng: number | null
}

export interface ArbeitgeberAnsprechpartner {
  id: string
  arbeitgeber_id: string
  name: string
  position: string | null
  email: string | null
  telefon: string | null
  ist_hauptkontakt: boolean
  notizen: string | null
}

// ---- Stellen ----
export interface Stelle {
  id: string
  created_at: string
  updated_at: string

  arbeitgeber_id: string | null
  standort_id: string | null

  titel: string
  einrichtungstyp: string | null
  ort: string | null
  bundesland: string | null
  plz: string | null
  einsatzbereich: string | null
  arbeitsmodell: string | null
  schichtmodell: string | null
  nacht_erforderlich: boolean | null
  erforderliche_qualifikation: string | null
  gehalt_min: number | null
  gehalt_max: number | null

  // Matching v2: Arbeitsbedingungen
  anerkennung_erforderlich: string | null
  sprachlevel: string | null
  mindestberufserfahrung: number | null
  wochenstunden_min: number | null
  wochenstunden_max: number | null
  nachtdienst_erforderlich: string | null
  wochenenddienst_erforderlich: string | null
  geteilte_dienste: string | null
  rufbereitschaft: string | null
  startdatum: string | null
  flexibler_start: boolean | null
  zuschlaege: string | null
  benefits: string[] | null
  probezeit: string | null
  befristung: boolean | null
  slider_antworten: SliderAntworten | null
  fakten: Record<string, unknown> | null

  quelle: string | null
  quell_url: string | null
  datum_erfassung: string | null

  status: string
  ist_duplikat: boolean
  duplikat_von: string | null

  beschreibung: string | null
  interne_notizen: string | null

  // Joined data
  arbeitgeber?: Arbeitgeber
}

// ---- Matches ----
export interface Match {
  id: string
  created_at: string

  pflegekraft_id: string
  stelle_id: string | null
  arbeitgeber_id: string | null

  score: number
  kategorie: string
  muss_kriterien_erfuellt: boolean

  // Matching v2
  score_hard_facts: number | null
  score_arbeitsplatz: number | null
  score_retention: number | null
  risiko_abzug: number | null
  knockout: boolean | null
  berechnung: MatchResult | null
  berechnet_am: string | null

  manuell_bestaetigt: boolean | null
  manuell_verworfen: boolean | null
  verworfen_grund: string | null

  bearbeitet_von: string | null
  bearbeitet_am: string | null

  // Joined data
  stelle?: Stelle
  arbeitgeber?: Arbeitgeber
  pflegekraft?: Pflegekraft
}

// ---- Pipeline ----
export interface PipelineEvent {
  id: string
  created_at: string

  pflegekraft_id: string
  stelle_id: string | null
  arbeitgeber_id: string | null
  match_id: string | null

  event_typ: string
  notizen: string | null
  erstellt_von: string | null
  naechster_schritt: string | null
  naechster_schritt_datum: string | null

  pflegekraft?: Pflegekraft
  stelle?: Stelle
  arbeitgeber?: Arbeitgeber
}

// ---- Admin Users ----
export interface AdminUser {
  id: string
  rolle: 'admin' | 'recruiter'
  name: string
  created_at: string
}

/* ============================================
   FUNNEL-DATEN — Pflegekraft
   ============================================ */

export interface FunnelData {
  // Phase 1: Grunddaten
  vorname: string
  nachname: string
  telefon_vorwahl: string
  telefon: string
  email: string
  plz: string
  wohnort: string
  bundesland: string
  fuehrerschein: boolean | null
  eigener_pkw: boolean | null

  // Phase 2: Beruf & Qualifikation
  qualifikation: string
  qualifikation_sonstiges: string
  anerkennungsstatus: string
  berufserfahrung_jahre: number | null
  gewuenschte_bereiche: string[]

  // Phase 3: Arbeitszeit & Rahmen
  gewuenschte_wochenstunden: number | null
  gewuenschte_arbeitsmodelle: string[]
  fruehester_wechselzeitpunkt: string
  max_arbeitsweg_km: number | null
  umzug_moeglich: boolean | null
  umzug_wohin: string
  mindestgehalt_brutto: number | null
  gehaltswunsch_brutto: number | null
  nachtdienst_moeglich: string
  wochenenddienst_moeglich: string
  geteilte_dienste_ok: string
  rufbereitschaft_ok: string

  // Phase 4: Wechselgründe
  wechselgruende: string[]
  wechselgrund_intensitaet: Record<string, number>
  wechselgrund_top: string[]
  wechselgruende_sonstiges: string

  // Phase 5: Slider-Profil (alle Kategorien)
  slider_antworten: SliderAntworten

  // Phase 6: No-Gos & Wechselbegleitung
  no_gos: string[]
  trial_day_interesse: string
  umzugs_support: string
  anerkennungs_support: string
  freitext_ausschluss: string
  freitext_wichtig: string

  // Phase 7: Abschluss
  einwilligung_datenschutz: boolean
  einwilligung_kontakt: boolean
  einwilligung_anonymisiert: boolean
}

export const EMPTY_FUNNEL_DATA: FunnelData = {
  vorname: '',
  nachname: '',
  telefon_vorwahl: '+49',
  telefon: '',
  email: '',
  plz: '',
  wohnort: '',
  bundesland: '',
  fuehrerschein: null,
  eigener_pkw: null,

  qualifikation: '',
  qualifikation_sonstiges: '',
  anerkennungsstatus: '',
  berufserfahrung_jahre: null,
  gewuenschte_bereiche: [],

  gewuenschte_wochenstunden: null,
  gewuenschte_arbeitsmodelle: [],
  fruehester_wechselzeitpunkt: '',
  max_arbeitsweg_km: null,
  umzug_moeglich: null,
  umzug_wohin: '',
  mindestgehalt_brutto: null,
  gehaltswunsch_brutto: null,
  nachtdienst_moeglich: '',
  wochenenddienst_moeglich: '',
  geteilte_dienste_ok: '',
  rufbereitschaft_ok: '',

  wechselgruende: [],
  wechselgrund_intensitaet: {},
  wechselgrund_top: [],
  wechselgruende_sonstiges: '',

  slider_antworten: {},

  no_gos: [],
  trial_day_interesse: '',
  umzugs_support: '',
  anerkennungs_support: '',
  freitext_ausschluss: '',
  freitext_wichtig: '',

  einwilligung_datenschutz: false,
  einwilligung_kontakt: false,
  einwilligung_anonymisiert: false,
}

/* ============================================
   FUNNEL-DATEN — Arbeitgeber (Einrichtungsprofil)
   ============================================ */

export interface ArbeitgeberFunnelData {
  // Phase 1: Einrichtung
  unternehmensname: string
  einrichtungstyp: string
  plz: string
  ort: string
  bundesland: string
  ansprechpartner_name: string
  ansprechpartner_position: string
  ansprechpartner_email: string
  ansprechpartner_telefon: string

  // Phase 2: Bedarf an Pflegekräften
  gewuenschte_qualifikationen: string[]
  einsatzbereiche: string[]
  arbeitsmodelle: string[]
  wochenstunden_min: number | null
  wochenstunden_max: number | null
  mindestberufserfahrung: number | null
  anerkennung_erforderlich: string
  sprachlevel: string

  // Phase 3: Vergütung & Benefits
  gehalt_min: number | null
  gehalt_max: number | null
  zuschlaege: string
  benefits: string[]

  // Phase 4: Dienstplan-Realität
  nachtdienst_erforderlich: string
  wochenenddienst_erforderlich: string
  geteilte_dienste: string
  rufbereitschaft: string
  dienstplan_vorlauf_wochen: number | null
  ueberstunden_pro_monat: number | null
  einspringen_haeufigkeit: string
  wochenend_frequenz: string

  // Phase 5: Faktencheck
  digitale_doku: boolean | null
  hilfskraefte_vorhanden: boolean | null
  hebehilfen_vorhanden: boolean | null
  betreuungsschluessel: string
  feedback_rhythmus: string
  praxisanleitung: boolean | null

  // Phase 6: Arbeitsbedingungen (Slider-Profil)
  slider_antworten: SliderAntworten

  // Phase 7: Onboarding & Wechselbegleitung
  onboarding_dauer_wochen: number | null
  feste_ansprechperson: boolean | null
  hospitation_moeglich: boolean | null
  umzugshilfe: string
  anerkennungshilfe: string
  besonderheiten: string

  // Phase 8: Abschluss
  einwilligung_datenschutz: boolean
  einwilligung_kontakt: boolean
}

export const EMPTY_AG_FUNNEL_DATA: ArbeitgeberFunnelData = {
  unternehmensname: '',
  einrichtungstyp: '',
  plz: '',
  ort: '',
  bundesland: '',
  ansprechpartner_name: '',
  ansprechpartner_position: '',
  ansprechpartner_email: '',
  ansprechpartner_telefon: '',

  gewuenschte_qualifikationen: [],
  einsatzbereiche: [],
  arbeitsmodelle: [],
  wochenstunden_min: null,
  wochenstunden_max: null,
  mindestberufserfahrung: null,
  anerkennung_erforderlich: '',
  sprachlevel: '',

  gehalt_min: null,
  gehalt_max: null,
  zuschlaege: '',
  benefits: [],

  nachtdienst_erforderlich: '',
  wochenenddienst_erforderlich: '',
  geteilte_dienste: '',
  rufbereitschaft: '',
  dienstplan_vorlauf_wochen: null,
  ueberstunden_pro_monat: null,
  einspringen_haeufigkeit: '',
  wochenend_frequenz: '',

  digitale_doku: null,
  hilfskraefte_vorhanden: null,
  hebehilfen_vorhanden: null,
  betreuungsschluessel: '',
  feedback_rhythmus: '',
  praxisanleitung: null,

  slider_antworten: {},

  onboarding_dauer_wochen: null,
  feste_ansprechperson: null,
  hospitation_moeglich: null,
  umzugshilfe: '',
  anerkennungshilfe: '',
  besonderheiten: '',

  einwilligung_datenschutz: false,
  einwilligung_kontakt: false,
}
